import { useState, type FormEvent } from 'react';
import { CheckCircle, Loader, Phone, ArrowRight } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { captureAttribution } from '../../utils/attribution';

import { trackLeadConversion } from '../../utils/analytics';
import { COUNTRY_CODES, toInternationalDigits } from '../../utils/phone';
import { useVoiceConsent, VoiceConsentCheckbox } from '../forms/VoiceConsent';
import { todaySast } from '../../utils/sastDate';
interface CallbackFormData {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  countryCode: string;
  preferredDate: string;
  preferredTime: string;
  message: string;
}

const initialFormData: CallbackFormData = {
  firstName: '',
  lastName: '',
  email: '',
  mobileNumber: '',
  countryCode: '27',
  preferredDate: '',
  preferredTime: '',
  message: '',
};

export default function CallbackBookingSection() {
  const [formData, setFormData] = useState<CallbackFormData>(initialFormData);
  const voice = useVoiceConsent();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const section = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });

  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  // SA today: the picker must not offer yesterday (UTC date before 02:00 SAST) or hide today abroad.
  const today = todaySast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Full international digits (shared normaliser — see utils/phone.ts)
      const mobileNumber = toInternationalDigits(formData.mobileNumber, formData.countryCode);

      const attribution = captureAttribution();
      const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/websiteCallbackRequest`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...voice.payload,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          mobileNumber,
          preferredDate: formData.preferredDate,
          preferredTime: formData.preferredTime,
          message: formData.message.trim(),
          attribution,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(data.error || `HTTP error ${response.status}`);
      }

      setSubmitted(true);
      trackLeadConversion('callback_request');
      setFormData(initialFormData);
    } catch (err: any) {
      console.error('Callback form submission error:', err);
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="section-padding bg-white">
        <div className="section-container px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto bg-gray-50 border border-gray-200 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
            <p className="text-gray-600">
              Your callback has been booked. One of our property consultants will contact you at your preferred time.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const inputClass =
    'w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors text-gray-900 placeholder-gray-400';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <section className="section-padding bg-white">
      <div ref={section.ref} className="section-container px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className={`text-center mb-10 reveal ${section.isVisible ? 'visible' : ''}`}>
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6 text-sm font-medium text-primary">
            <Phone size={14} />
            Free Consultation
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight mb-3">
            Request a Callback From an Expert
          </h2>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Choose a date and time that works for you and one of our property investment experts will reach out.
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className={`max-w-2xl mx-auto bg-gray-50 border border-gray-200 rounded-2xl p-6 md:p-8 space-y-5 reveal ${section.isVisible ? 'visible' : ''}`}
          style={{ transitionDelay: '0.15s' }}
        >
          {/* Name fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                placeholder="Enter your first name"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                Surname <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                placeholder="Enter your surname"
                className={inputClass}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className={labelClass}>
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your.email@example.com"
              className={inputClass}
            />
          </div>

          {/* Mobile Number with Country Code */}
          <div>
            <label className={labelClass}>
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-2">
              <select
                id="countryCode"
                name="countryCode"
                aria-label="Country code"
                value={formData.countryCode}
                onChange={handleChange}
                className="w-28 px-3 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors text-gray-900"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <input
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                required
                placeholder="82 123 4567"
                className={`flex-1 min-w-0 ${inputClass}`}
              />
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="preferredDate" className={labelClass}>
                Preferred Date <span className="text-red-500">*</span>
              </label>
              <input
                id="preferredDate"
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                required
                min={today}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="preferredTime" className={labelClass}>
                Preferred Time (SA time) <span className="text-red-500">*</span>
              </label>
              <select
                id="preferredTime"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                required
                className={inputClass}
              >
                <option value="">Select a time</option>
                {Array.from({ length: 24 * 4 }, (_, i) => {
                  const h = String(Math.floor(i / 4)).padStart(2, '0');
                  const m = String((i % 4) * 15).padStart(2, '0');
                  return <option key={i} value={`${h}:${m}`}>{`${h}:${m}`}</option>;
                })}
              </select>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className={labelClass}>
              Message <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={3}
              placeholder="Any specific topics you'd like to discuss?"
              className={inputClass}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Submit */}
          <VoiceConsentCheckbox consent={voice.consent} ticked={voice.ticked} setTicked={voice.setTicked} className="mb-3" />
          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-semibold px-6 py-3.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-primary/25 hover:shadow-xl hover:-translate-y-0.5 group"
          >
            {submitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Booking...
              </>
            ) : (
              <>
                Book My Callback
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
